/**
 * The link a rep texts a customer, and what can be believed about what comes
 * back through it.
 *
 * Jonathon, 2026-09-19: "i would like a link i can text a customer where they
 * can enter in their own info and their lead goes into roofboard."
 *
 * Everything here is a pure decision about a stranger's typing: what counts as
 * a usable submission, what their sentence about hearing of us matches on the
 * lead source list, how their chosen days and windows read back to a rep. The
 * web form, the `lead-link` Edge Function and the tests all import it, so the
 * form cannot validate one thing while the server accepts another (rule 5).
 *
 * The one thing deliberately *not* here is trust. Everything below is also
 * checked on the server: a browser form is a suggestion, not a gate.
 */

import { US_STATES } from "./constants.js";

/* ------------------------------------------------------------- the slug -- */

/** How long an intake link's slug may be. Short enough to text, long enough
 * to be somebody's name. */
export const INTAKE_SLUG_MIN = 2;
export const INTAKE_SLUG_MAX = 32;

/**
 * Slugs the app's own routes already answer to.
 *
 * The intake page lives under `/go/<slug>`, so nothing here can actually
 * collide today — but a slug is also what somebody types when they are asked
 * for one, and "login" or "admin" as a rep's personal link is a trap waiting
 * for the day the route moves. Refusing them costs nothing.
 */
export const RESERVED_INTAKE_SLUGS = [
  "admin",
  "api",
  "app",
  "auth",
  "go",
  "help",
  "job",
  "jobs",
  "login",
  "new",
  "p",
  "s",
  "storms",
  "tools",
];

/**
 * A slug reduced to the form the database will accept, or null if there is
 * nothing usable left.
 *
 * Lower case, spaces and punctuation to hyphens, no leading or trailing
 * hyphen. "Sam Sandoval" becomes "sam-sandoval"; "  SAM!! " becomes "sam".
 */
export function normaliseIntakeSlug(value: string): string | null {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length < INTAKE_SLUG_MIN || slug.length > INTAKE_SLUG_MAX)
    return null;
  return slug;
}

/** Why a slug cannot be used, in words the person choosing it can act on, or
 * null when it is fine. */
export function intakeSlugProblem(value: string): string | null {
  const slug = normaliseIntakeSlug(value);
  if (slug === null) {
    return `A link name is ${INTAKE_SLUG_MIN} to ${INTAKE_SLUG_MAX} letters or numbers.`;
  }
  if (RESERVED_INTAKE_SLUGS.includes(slug)) {
    return `"${slug}" is already used by the app itself. Pick another.`;
  }
  return null;
}

/* ----------------------------------------------------------- the answers -- */

/**
 * What the customer can tap on the second screen.
 *
 * Jonathon named these, 2026-09-19: the roof work that was already there, plus
 * siding and decking both ways. The order is the order they are tapped in —
 * storm damage first because that is what the text was usually about.
 */
export const INTAKE_WANTS = [
  "Storm damage",
  "Roof repair",
  "Roof replacement",
  "Siding repair",
  "Siding replacement",
  "Deck repair",
  "Deck replacement",
  "Gutters",
  "Not sure",
];

/** The windows offered on the third screen. Three, because a customer picking
 * from a list of eight picks nothing. */
export const INTAKE_TIME_WINDOWS = [
  "Morning 8–12",
  "Midday 12–3",
  "Afternoon 3–6",
];

/**
 * How many days the form offers.
 *
 * Seven, and they are seven days somebody could actually come out — Jonathon,
 * 2026-09-22: "don't show saturdays, sundays, or major us holidays". So this
 * counts working days, not squares on a calendar: asked on a Thursday, the
 * form offers Friday and then the whole of the following week rather than a
 * Saturday and a Sunday nobody can use.
 */
export const INTAKE_DAYS_AHEAD = 7;

/** At most this many photographs. Enough for a roof and the ceiling under it;
 * few enough that a submission over a cell signal still finishes. */
export const INTAKE_PHOTO_LIMIT = 8;

/** The longest edge a photograph is scaled to before it is sent. A modern
 * phone's 4000px original is four times the size for no more detail than
 * anybody reads it at. */
export const INTAKE_PHOTO_MAX_EDGE = 1600;

/* ------------------------------------------------------ the days offered -- */

/** An ISO date as a Date pinned to midday UTC, so no timezone can shunt it
 * onto the day before or the day after. */
function atNoon(iso: string): Date {
  return new Date(`${iso}T12:00:00Z`);
}

/** A Date as its ISO date, read in UTC to match `atNoon`. */
function isoOf(day: Date): string {
  return [
    day.getUTCFullYear(),
    String(day.getUTCMonth() + 1).padStart(2, "0"),
    String(day.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

/** The nth given weekday of a month, as an ISO date. `weekday` is 0 for
 * Sunday. */
function nthWeekdayOf(
  year: number,
  month: number,
  weekday: number,
  nth: number,
): string {
  const first = new Date(Date.UTC(year, month, 1, 12));
  const shift = (weekday - first.getUTCDay() + 7) % 7;
  return isoOf(new Date(Date.UTC(year, month, 1 + shift + (nth - 1) * 7, 12)));
}

/** The last given weekday of a month, as an ISO date. */
function lastWeekdayOf(year: number, month: number, weekday: number): string {
  const last = new Date(Date.UTC(year, month + 1, 0, 12));
  const back = (last.getUTCDay() - weekday + 7) % 7;
  return isoOf(new Date(Date.UTC(year, month + 1, 0 - back, 12)));
}

/**
 * A fixed-date holiday, moved to the weekday it is kept on.
 *
 * The Fourth of July on a Saturday is taken on the Friday and on a Sunday on
 * the Monday. Without this, a holiday that lands at the weekend would be
 * dropped by the weekend rule and the working day everybody is actually off
 * would still be offered.
 */
function observed(year: number, month: number, date: number): string {
  const day = new Date(Date.UTC(year, month, date, 12));
  if (day.getUTCDay() === 6) day.setUTCDate(day.getUTCDate() - 1);
  else if (day.getUTCDay() === 0) day.setUTCDate(day.getUTCDate() + 1);
  return isoOf(day);
}

/**
 * The days CopperRidge does not knock on a door.
 *
 * The ones a customer would think it odd to be offered, not the full federal
 * list. Three of the choices are Jonathon's rather than the calendar's:
 * Thanksgiving takes the Friday with it, because in this trade it always
 * does; Christmas Eve and New Year's Eve are off, 2026-09-22 ("dont show new
 * years eve or christmas eve"); and **Juneteenth is a working day**, same
 * message — "we work juneteenth" — so it is deliberately absent rather than
 * forgotten.
 *
 * The two eves are the plain date and never `observed`: an eve that falls at
 * a weekend is already not offered, and nothing is taken in lieu of it. They
 * are listed first so that where one collides with a holiday kept in lieu —
 * Christmas Day 2027 is a Saturday, so the Friday is both — the holiday's own
 * name wins.
 */
export function usHolidaysIn(year: number): Record<string, string> {
  const thanksgiving = nthWeekdayOf(year, 10, 4, 4);
  const dayAfter = isoOf(
    new Date(atNoon(thanksgiving).getTime() + 24 * 60 * 60 * 1000),
  );

  return {
    [isoOf(new Date(Date.UTC(year, 11, 24, 12)))]: "Christmas Eve",
    [isoOf(new Date(Date.UTC(year, 11, 31, 12)))]: "New Year's Eve",
    [observed(year, 0, 1)]: "New Year's Day",
    [nthWeekdayOf(year, 0, 1, 3)]: "Martin Luther King Jr. Day",
    [lastWeekdayOf(year, 4, 1)]: "Memorial Day",
    [observed(year, 6, 4)]: "Independence Day",
    [nthWeekdayOf(year, 8, 1, 1)]: "Labor Day",
    [observed(year, 10, 11)]: "Veterans Day",
    [thanksgiving]: "Thanksgiving",
    [dayAfter]: "the day after Thanksgiving",
    [observed(year, 11, 25)]: "Christmas Day",
  };
}

/**
 * What is being kept on this date, or null on an ordinary day.
 *
 * Both this year's list and next year's are consulted, because New Year's Day
 * can be kept in the year before it: 1 January 2028 is a Saturday, so the day
 * off is Friday 31 December 2027, which `usHolidaysIn(2028)` holds and
 * `usHolidaysIn(2027)` does not.
 */
export function usHolidayName(iso: string): string | null {
  const day = atNoon(iso);
  if (Number.isNaN(day.getTime())) return null;
  const year = day.getUTCFullYear();
  return usHolidaysIn(year)[iso] ?? usHolidaysIn(year + 1)[iso] ?? null;
}

/** Whether somebody could come out on this date: a weekday, and not a holiday
 * anybody keeps. Jonathon, 2026-09-22. */
export function isBookableDay(iso: string): boolean {
  const day = atNoon(iso);
  if (Number.isNaN(day.getTime())) return false;
  const weekday = day.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return usHolidayName(iso) === null;
}

/**
 * The days the form offers, starting the day after `from`.
 *
 * Working days only, so the count is a count of usable days rather than of
 * squares on a calendar. It looks at most eight weeks ahead before giving up,
 * which cannot happen with any real holiday calendar and stops a bad date
 * turning into a loop that never ends.
 */
export function offeredIntakeDays(
  from: Date,
  howMany: number = INTAKE_DAYS_AHEAD,
): string[] {
  const days: string[] = [];
  const cursor = new Date(
    Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 12),
  );
  for (let step = 0; step < 56 && days.length < howMany; step++) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const iso = isoOf(cursor);
    if (isBookableDay(iso)) days.push(iso);
  }
  return days;
}

/* ------------------------------------------------------- the lead source -- */

/**
 * What a typed sentence has to contain to count as naming a lead source.
 *
 * Jonathon, 2026-09-19: "keep their typed sentence but if it's obvious select
 * one of the drop down lead source options."
 *
 * "Obvious" is the whole design. These are the words somebody actually writes
 * in that box, and each one only claims a source when the source exists and is
 * active on this database — the list is configuration and has been edited.
 * Nothing clever: a guess that is wrong here becomes a number in a report that
 * nobody can trace back, which is worse than no guess at all.
 *
 * Order matters. A sentence can hit two rules — "my neighbour knocked on my
 * door" — and the first match wins, so the more specific source is listed
 * first.
 */
export const LEAD_SOURCE_HINTS: { source: string; words: string[] }[] = [
  {
    source: "Referral-Friend/Neighbor",
    words: [
      "neighbor",
      "neighbour",
      "friend",
      "referr",
      "referal",
      "recommend",
      "told me about",
      "sent me",
      "my sister",
      "my brother",
      "coworker",
      "co-worker",
      "work with",
    ],
  },
  {
    source: "Repeat Customer",
    words: [
      "used you before",
      "used yall before",
      "did my roof",
      "did our roof",
      "last time",
      "you did work",
      "repeat",
      "previous",
      "again",
    ],
  },
  {
    source: "Google LSA",
    words: ["google", "searched", "search", "looked you up", "internet"],
  },
  {
    source: "Door Knocking",
    words: [
      "knocked",
      "knocking",
      "came by",
      "came to my door",
      "at my door",
      "in the neighborhood",
      "in the neighbourhood",
    ],
  },
  { source: "Yard Sign", words: ["yard sign", "sign in the yard", "a sign"] },
  { source: "Website", words: ["website", "your site", "web site", "online"] },
  { source: "Truck", words: ["your truck", "the truck", "your van"] },
];

/**
 * The lead source to record for a lead that came through a link.
 *
 * `active` is the list as it stands on this database — names only. A hint that
 * names a source nobody has any more matches nothing, which is the point: the
 * list is the authority and this only ever picks off it.
 *
 * Nothing obvious gives back `Rep Link`, which is a real value and is the
 * truthful answer — it came through somebody's link and they did not say
 * anything we recognised. It is never a silent blank: a job with no lead
 * source falls out of every report that counts them.
 */
export const REP_LINK_SOURCE = "Rep Link";

export function matchLeadSource(
  typed: string | null | undefined,
  active: readonly string[],
): { source: string; matched: boolean } {
  const said = (typed ?? "").toLowerCase();
  if (said.trim() !== "") {
    for (const hint of LEAD_SOURCE_HINTS) {
      if (!active.includes(hint.source)) continue;
      if (hint.words.some((word) => said.includes(word))) {
        return { source: hint.source, matched: true };
      }
    }
  }
  return { source: REP_LINK_SOURCE, matched: false };
}

/* --------------------------------------------------------- the submission -- */

/** What a customer's phone sends us, before anything has been believed. */
export interface IntakeSubmission {
  firstName?: unknown;
  lastName?: unknown;
  phone?: unknown;
  email?: unknown;
  /** The street line only. City, state and ZIP are their own boxes. */
  address?: unknown;
  city?: unknown;
  state?: unknown;
  zip?: unknown;
  wants?: unknown;
  notes?: unknown;
  preferredDays?: unknown;
  preferredTimes?: unknown;
  heardHow?: unknown;
  /** The honeypot. A person never sees this field; a form-filling script
   * fills everything. Anything in it means the submission is not a person. */
  company?: unknown;
}

/** A submission that has been believed, in the shape `record_intake_lead`
 * takes. */
export interface CleanIntake {
  firstName: string;
  lastName: string | null;
  /** First and last joined, which is what `contacts.full_name` ends up as and
   * what the notification says. Never derived again anywhere else. */
  name: string;
  phone: string;
  email: string | null;
  /** The street line. */
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  wants: string[];
  notes: string | null;
  preferredDays: string[];
  preferredTimes: string[];
  heardHow: string | null;
}

const text = (value: unknown, limit: number): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().slice(0, limit);
  return trimmed === "" ? null : trimmed;
};

const list = (value: unknown, allowed: readonly string[]): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item === "string" && allowed.includes(item)) seen.add(item);
  }
  // Back in the order they are offered, not the order they arrived, so two
  // leads that chose the same things read the same on the board.
  return allowed.filter((item) => seen.has(item));
};

/** Ten digits, or null. The database's own CHECK says the same thing; this is
 * so the customer is told rather than shown an error page. */
export function intakePhoneDigits(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits.length === 10 ? digits : null;
}

/**
 * A ZIP as the customer typed it into the ZIP box, or null.
 *
 * Five digits, or five-plus-four reduced to the five the rest of the app
 * stores. Anything else is left out rather than written through: the column
 * has a CHECK that would refuse it, and refusing a whole lead over a mistyped
 * ZIP would be the tail wagging the dog.
 */
export function intakeZip(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = /^\s*(\d{5})(?:-\d{4})?\s*$/.exec(value);
  return match ? (match[1] ?? null) : null;
}

/**
 * A state, as the two letters every other address in Roof Board is stored
 * with.
 *
 * "al", "AL", "Alabama" and " Ala " all become "AL". Somewhere we have never
 * worked and cannot recognise is left null rather than guessed — a wrong state
 * on a job address sends somebody to the wrong county.
 */
export function intakeState(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const said = value.trim().toUpperCase();
  if (said === "") return null;

  const codes = Object.values(US_STATES);
  if (codes.includes(said)) return said;

  const spelled = Object.entries(US_STATES).find(
    ([name]) => name.toUpperCase() === said,
  );
  return spelled ? spelled[1] : null;
}

/** An ISO date (YYYY-MM-DD) that is today or later, or null. A day in the past
 * is either a stale page left open overnight or somebody editing the form. */
export function intakeDay(value: unknown, today: Date): string | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return null;
  const floor = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );
  const day = new Date(`${value}T00:00:00Z`);
  if (
    Number.isNaN(day.getTime()) ||
    day.toISOString().slice(0, 10) !== value ||
    day < floor
  )
    return null;
  // A day more than a month out is not one this form offered.
  const ceiling = new Date(floor);
  ceiling.setUTCDate(ceiling.getUTCDate() + 31);
  if (day > ceiling) return null;
  // And not a day the form never offered. A Sunday in a submission did not
  // come from a person tapping the form, and a lead saying the customer is
  // free on Christmas Day is worse than one saying nothing.
  return isBookableDay(value) ? value : null;
}

/**
 * What the form may send, reduced to what may be stored — or the reason it
 * cannot be, in words the customer reads on their own screen.
 *
 * Only a first name and a usable mobile are required. Jonathon, 2026-09-19,
 * on the email: "don't make it required for them to move forward but I dont
 * want to encourage the customer not to provide it" — so it is asked for
 * plainly, with no "optional" beside it, and a blank one is accepted. The same
 * goes for the last name and for every part of the address: a customer who
 * gives a first name and a number can be rung up, and that is the whole point
 * of the link.
 */
export function readIntakeSubmission(
  body: IntakeSubmission,
  today: Date,
): { ok: true; lead: CleanIntake } | { ok: false; reason: string } {
  // The honeypot, before anything else. Nothing that fills it gets an error
  // worth learning from.
  if (text(body.company, 100) !== null) {
    return { ok: false, reason: "Sorry — that did not go through." };
  }

  const firstName = text(body.firstName, 80);
  if (firstName === null) return { ok: false, reason: "We need your name." };
  const lastName = text(body.lastName, 80);

  const phone = intakePhoneDigits(body.phone);
  if (phone === null) {
    return {
      ok: false,
      reason: "We need a ten-digit cell number to call you.",
    };
  }

  const email = text(body.email, 200);

  const days: string[] = [];
  if (Array.isArray(body.preferredDays)) {
    for (const value of body.preferredDays.slice(0, 31)) {
      const day = intakeDay(value, today);
      if (day !== null && !days.includes(day)) days.push(day);
    }
    days.sort();
  }

  return {
    ok: true,
    lead: {
      firstName,
      lastName,
      // What `contacts.full_name` will end up as, built once, here. Every
      // caller that needs a display name takes this rather than joining the
      // parts again and arriving at a different answer.
      name: [firstName, lastName].filter(Boolean).join(" "),
      phone,
      email: email !== null && email.includes("@") ? email : null,
      address: text(body.address, 300),
      city: text(body.city, 120),
      state: intakeState(body.state),
      zip: intakeZip(body.zip),
      wants: list(body.wants, INTAKE_WANTS),
      notes: text(body.notes, 2000),
      preferredDays: days,
      preferredTimes: list(body.preferredTimes, INTAKE_TIME_WINDOWS),
      heardHow: text(body.heardHow, 500),
    },
  };
}

/* -------------------------------------------------------- what gets said -- */

/** A day, as a rep reads it on the job: "Tue 23 Sep". */
export function intakeDayLabel(iso: string): string {
  const day = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(day.getTime())) return iso;
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
    day.getUTCDay()
  ];
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][day.getUTCMonth()];
  return `${weekday} ${day.getUTCDate()} ${month}`;
}

/** The days and windows on one line: "Tue 23 Sep, Wed 24 Sep — Midday or
 * Afternoon". Empty string when they said nothing, so a caller can hide the
 * whole row rather than print a label with nothing after it. */
export function intakeWhenLine(
  days: readonly string[],
  times: readonly string[],
): string {
  if (days.length === 0 && times.length === 0) return "";
  const when = days.map(intakeDayLabel).join(", ");
  const window = times.map((time) => time.split(" ")[0]).join(" or ");
  if (when === "") return window;
  if (window === "") return when;
  return `${when} — ${window}`;
}

/**
 * The notification, for the people who asked to be told.
 *
 * `url` is the whole point of it: Jonathon, 2026-09-19, "make the notification
 * be to where we can click on it and see everything the customer put in
 * there". The service worker navigates to whatever is here, and `#job=` is
 * the job screen, which is where everything they typed already is.
 *
 * `tag` is the job, so a second notification about the same lead replaces the
 * first rather than stacking under it.
 */
export function intakePushMessage(lead: {
  name: string;
  city?: string | null;
  wants?: readonly string[] | null;
  photoCount?: number;
  repName?: string | null;
  jobId: string;
}): { title: string; body: string; url: string; tag: string } {
  const photos = lead.photoCount ?? 0;
  const detail = [
    lead.city ?? null,
    lead.wants && lead.wants.length > 0 ? lead.wants.join(", ") : null,
    photos > 0 ? `${photos} photo${photos === 1 ? "" : "s"}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return {
    title: lead.repName
      ? `New lead for ${lead.repName}`
      : "New lead — unclaimed",
    body: detail === "" ? lead.name : `${lead.name} — ${detail}`,
    url: `/#job=${lead.jobId}`,
    tag: `lead-${lead.jobId}`,
  };
}

/** The caption a customer's photograph carries in CompanyCam, so nobody
 * mistakes it for an inspection photograph taken on site. */
export const INTAKE_PHOTO_CAPTION = "From customer";

/** The tag it carries there, for the same reason. */
export const INTAKE_PHOTO_TAG = "From customer";

/**
 * The image types a customer's photograph may be stored as, and the file
 * extension each one is kept under.
 *
 * An allowlist rather than `image/*`, and the stored content type is taken
 * from this table rather than from the upload. Both halves matter. The type on
 * a multipart part is whatever the sender wrote there, and it is what the file
 * is later served back as — so `image/svg+xml` passes a `startsWith("image/")`
 * test and is then handed to whoever opens it as a document that runs script.
 * The bucket is private and only a signed-in member of staff can read it
 * (ADR 0171), so that is a staff browser on the storage origin rather than a
 * stranger's, but it is not a thing to leave standing.
 *
 * Taking the extension from the same row is what stops the two disagreeing:
 * before this, every upload that was not a PNG was named `.jpg` whatever it
 * actually was, which is a small lie to anyone reading the bucket.
 *
 * Nothing a customer really sends is turned away. The form re-encodes every
 * photograph to JPEG on the phone before it uploads; the rest of the list is
 * what a file picker hands over when it has not.
 */
const INTAKE_PHOTO_TYPES = new Map<string, string>([
  ["image/jpeg", "jpg"],
  ["image/jpg", "jpg"],
  ["image/png", "png"],
  ["image/webp", "webp"],
  ["image/gif", "gif"],
  ["image/heic", "heic"],
  ["image/heif", "heif"],
]);

/**
 * The extension to store a photograph of this type under, or `null` if the
 * bucket will not hold it at all.
 *
 * A `Map` rather than an object literal because the key is a stranger's
 * string: `photoExtension("constructor")` on a plain object walks the
 * prototype chain and comes back with something truthy.
 */
export function intakePhotoExtension(type: unknown): string | null {
  if (typeof type !== "string") return null;
  return INTAKE_PHOTO_TYPES.get(type.trim().toLowerCase()) ?? null;
}
